// Angular
import { Component, ViewEncapsulation, OnInit, ViewChild, OnDestroy } from '@angular/core';
// Service
import { ImportOrderService } from './service/import-order.service';
import {
	BroadcastService,
	ToasterType,
	CONTENT_LOADER_EVENT,
	SignalRState,
	CustomDateCellComponent, AppService
} from '@cv-invision/core';
// Model
import { ImportFormModel } from './model/import-form.model';
// Other
import { Observable, Subject, of } from 'rxjs';
import { Store } from '@ngxs/store';
import { ImportColumnsModel } from './model/import-grid.model';
import { SetCurrentGridPage, SetIsImportingOrder } from './state/action/import.action';
import { ImportState } from './state/import.state';
import { takeUntil } from 'rxjs/operators';
import { OrderReviewResponse } from './state/model/import-state.model';
import { PaginationModel } from '@cv-invision/cv.component-library.ux';
import { CustomStatusImportCellComponent } from './custom-status-cell/custom-status-cell.component';
import { CONFIRM_ORDER_IMPORT } from './confirmation-import-modal/confirmation-import-modal.component';
export const IMPORT_ORDER = 'import-order';

@Component({
	selector: 'cv-import-order',
	encapsulation: ViewEncapsulation.None,
	templateUrl: './import-order.component.html',
	styleUrls: ['./import-order.component.scss']
})
export class ImportOrderComponent implements OnInit, OnDestroy {
	@ViewChild('importGrid', { static: true }) agGrid;
	public importForm: ImportFormModel = {
		number: null,
		app: null,
		form: null,
		paramName: null,
		deleteDuplicates: null
	};
	public appList$: Observable<any>;
	public formList$: Observable<any>;
	channels$: Observable<any> = this.store.select(SignalRState.channelEvents);
	reviewData$: Observable<OrderReviewResponse> = this.store.select(ImportState.getImportGridCollection);
	isSubmitingOrder$: Observable<boolean> = this.store.select(ImportState.getIsImportingOrder);
	gridData: Observable<any>;
	public gridOptions: any;
	public pagination: PaginationModel;
	private atComponentDestroy = new Subject();
	public isGridReady: boolean;
	public btnEnable: boolean;

	constructor(
		private importOrderService: ImportOrderService,
		private broadcastService: BroadcastService,
		private appService: AppService,
		private store: Store
	) {
		this.isGridReady = false;
		this.btnEnable = true;
		this.store.dispatch(new SetIsImportingOrder(this.isGridReady));
		this.pagination = {
			limit: 10,
			offset: 0,
			totalCount: 0
		};
		this.reviewData$.pipe(takeUntil(this.atComponentDestroy)).subscribe(result => {
			if (result) {
				this.gridData = of(result.data);
				this.pagination = {
					limit: result.limit,
					offset: result.offset,
					totalCount: result.totalCount
				};
			}
		});
		this.getData();
	}

	ngOnInit(): void {
		this.gridOptions = {
			rowHeight: 40,
			rowSelection: 'single',
			enableCellTextSelection: true,
			defaultColDef: {
				resizable: true,
				tooltipComponent: 'customStatusImportCellComponent'
			},
			localeText: {
				noRowsToShow: 'No requested orders to show',
				loadingOoo: 'Loading requested orders...'
			},
			columnDefs: new ImportColumnsModel().columns,
			frameworkComponents: {
				customStatusImportCellComponent: CustomStatusImportCellComponent,
				customDateCellComponent: CustomDateCellComponent
			}
		};

		this.gridReady();
		this.isSubmitingOrder$.pipe(takeUntil(this.atComponentDestroy)).subscribe(isSubmiting => {
			if (isSubmiting) {
				this.importForm.deleteDuplicates = this.store.selectSnapshot(ImportState.getIsSofDelete);
				this.submitOrder();
			} else {
				this.btnEnable = true;
			}
		});
		this.channels$.pipe(takeUntil(this.atComponentDestroy)).subscribe((channels: any) => {
			if (channels && channels[`orders`]) {
				this.setGirdOrderStatus(channels[`orders`].events);
			}
		});
	}

	/**
	 * wait until the grid elements load properly
	 */
	private gridReady(): void {
		// for some reason the 'sizeColumnsToFit' is not working fine when we go back from inbox section
		// this is hack to avoid a bad rendering
		setTimeout(() => {
			this.isGridReady = true;
		}, 500);
	}

	/**
	 *	set fail or Completed status for orders in the grid after import action
	 * @param channels
	 */
	public setGirdOrderStatus(channels: Array<any>): void {
		const orders = channels.filter(o => this.appService.getSignalExceptionEvents().includes(o.event));
		if (orders && orders.length) {
			const ordersList = this.store.selectSnapshot(ImportState.getImportGridCollection);
			const { data } = ordersList;
			const datafiltered = orders.filter(d => data.find(o => o.id === d.data.requestId));

			if (datafiltered.length) {
				this.store.dispatch(new SetCurrentGridPage(0));
			}
		}
	}

	/**
	 * Prepare the data that will be used of the Import Dropdowns
	 */
	public getData(): void {
		this.appList$ = this.importOrderService.getApps();
		this.formList$ = this.importOrderService.getForms();
		this.store.dispatch(new SetCurrentGridPage(0));
	}

	/**
	 * When the grid has just started, we make the content to fit
	 */
	public onInitializeGrid(params: any): void {
		this.agGrid = params.api;
		this.resizeToFitColumns();
	}

	/**
	 * Get the new instance of the Pagination and Execute the new Query
	 * @param paginationModel
	 */
	public onPaginationChange(paginationModel: PaginationModel): void {
		this.pagination = paginationModel;
		this.store.dispatch(new SetCurrentGridPage(paginationModel.offset));
	}

	/**
	 * Get the Appraisal Number
	 * @param appraisalNumber
	 */
	public onAppraisalChange(appraisalNumber: any): void {
		this.importForm.number = appraisalNumber.value !== '' ? appraisalNumber.value : null;
	}

	/**
	 * CC the value when selecting the App
	 * @param app
	 */
	public onAppSelectChange(app: any): void {
		this.importForm.app = app;
		this.importForm.paramName = Object.keys(this.importForm.app.parameters)[0];
	}

	/**
	 * CC the value when selecting the Form
	 * @param form
	 */
	public onFormSelectChange(form: any): void {
		this.importForm.form = form;
	}

	/**
	 * Create the New Order
	 */
	public onSubmitOrder(): void {
		const { form } = this.importForm.form;
		this.btnEnable = false;
		this.importOrderService.getDuplicateOrder(this.importForm.number, form).subscribe(d => {
			if (d.data.length) {
				this.showConfirmImport(d.data.pop(), d.totalCount);
			} else {
				this.submitOrder();
			}
		});
	}

	/**
	 * call submit order service
	 */
	private submitOrder(): void {
		this.importForm.deleteDuplicates = this.store.selectSnapshot(ImportState.getIsSofDelete);
		this.showLoader(true);
		this.importOrderService.importOrder(this.importForm).subscribe(
			(res: any) => {
				this.store.dispatch(new SetIsImportingOrder(false));
				this.showLoader(false);
				this.store.dispatch(new SetCurrentGridPage(0));

				this.broadcastService.broadcast({
					name: IMPORT_ORDER,
					event: {
						data: res.orderId
					}
				});

				setTimeout(() => {
					this.broadcastService.broadcast({
						name: ToasterType.SUCCESS,
						event: {
							message:
								'Import Process started: <span class="order-id">' +
								this.importForm.number +
								'</span>'
						}
					});
					this.importForm.number = null;
					this.btnEnable = true;
				}, 100);
			},
			error => {
				this.store.dispatch(new SetIsImportingOrder(false));
				this.showLoader(false);
				this.btnEnable = true;
			}
		);
	}

	/**
	 * Show/Hide the Loader
	 * @param isShowing
	 */
	private showLoader(isShowing: boolean): void {
		this.broadcastService.broadcast({
			name: CONTENT_LOADER_EVENT,
			event: {
				isLoader: isShowing,
				message: 'Submitting Order'
			}
		});
	}

	/**
	 * show a modal whether or not the order is duplicate
	 */
	private showConfirmImport(orderData: any, totalCount: number): void {
		this.broadcastService.broadcast({
			name: CONFIRM_ORDER_IMPORT,
			event: {
				orderData,
				totalCount
			}
		});
	}
	/**
	 * Return the label of the apprisal number field
	 * @param name
	 */
	public getAppraisalNumberLabel(name: string | null): string {
		let appraisalNumberLabel = 'App value';

		switch (name) {
			case 'AppraisalFileNumber':
				appraisalNumberLabel = 'Appraisal File Number';
				break;
			case 'OrderId':
				appraisalNumberLabel = 'Order Id';
				break;
		}

		return appraisalNumberLabel;
	}

	/**
	 * Every time the view port has change, we ensure the columns fits properly
	 */
	public resizeToFitColumns(): void {
		this.agGrid.sizeColumnsToFit();
	}

	/**
	 * Close all when the element is destroyed
	 * in case a Sub Component remains visible
	 */
	ngOnDestroy(): void {
		// Globally Destroy anything attached to it
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
